/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  AbstractFormComponent, BuilderNavigationService, CommonActions, CommunicationEntryPoint, ContactDetailsDialogConfig, contactDetailsDialogRoute, EmailDialogConfig, emailDialogRoute, FormActionPipelines, FormFlowState, PbComponentItemFinderDialogConfig, pbComponentItemFinderDialogRoute, PbComponentItemFinderListItem, PrinterBusinessService, PrinterChooseResult, whenFormInvalid, } from '@gh/builder-shared';
import {
  attachmentFromLetter, CommunicationType, ContactDeliveryMethod, ContactType, createLinkFromAttachment, createLinkFromLetter, JobVariationStatus, } from '@gh/builder-shared-data';
import { textMessageDialogRoute } from '@gh/common-internal-shared';
import {
  ActionFlow, AppRouter, compoundLogicalAction, condAction, eitherAction, exitAction, formStateSnapshot, FormStateSnapshot, getAllNewSelectedEntities, getFormStateSnapshotValue, getRouteInitData, getRouteParentTitle, LogicalAction, NumberTypes, queryRecalculationNeeded, sideEffectAction, streamOptionalValueAction, streamValueAction, } from '@gh/common-shared';
import { DirtyType, EMPTY_PRODUCE_STATUS, ID } from '@gh/common-shared-data';
import { PRODUCE_STATUS_CHECK_INTERVAL } from '@gh/config';
import { isMessageBasket, MessageBasket } from '@gh/core-messages';
import { TranslationService, TranslationTuple } from '@gh/core-mls';
import {
  Confirmation, DialogService, FormMode, noAsDefaultAnswer, NotificationService, TextMessageDialogConfig, WaitingService, yesAnswer, yesNoConfirmation, } from '@gh/core-ui';
import { createLink, differentFieldsWith, toString } from '@gh/core-util';
import { combineLatest$, interval$, of$, throwError$ } from '@gh/rx';
import { map$, catchError$, delay$, filter$, switchMap$, tap$ } from '@gh/rx/operators';

import { flatMap, flatten, isEmpty, padStart, stubTrue } from 'lodash';
import { Observable, Subscription } from 'rxjs';

import { JobCoDetailsBusinessService } from '../../resources/job-co-details-business.service';
import {
  isDocumentSetEmpty,
  isJobCoElementNotEnoughQuantityForMove,
  isPrintApprovedClaimedNeeded,
  isPrintJobOrderNeeded,
  isPrintQuoteNeeded,
  JobCoConfirmationItem,
  JobCoDetails,
  JobCoDetailsBottomInfo,
  JobCoDetailsGeneratedDocumentSet,
  JobCoDetailsMasterInfo,
  JobCoDetailsQuoteFilter,
  jobCoDocumentSetToEmailAttachments,
  JobCoElement,
  getJobCoElementId,
  isJobCoElement,
  isJobCoElementTotal,
  jobCoElementsFromFinder,
  JobCoLetter,
  jobCoPrintRequestFromGeneratedDocumentSet,
  JobCoProduceRequest,
  JobCoSave,
  JobCoUsePrinter,
  mergeJobCoDetailsWithQuoteFilter,
  produceRequestForPrintApprovedWithoutQuoteCopy,
  produceRequestForPrintApprovedWithQuoteCopy,
  usePrinterForAffectedOrders,
  usePrinterFromDeliveryMethod,
} from '../../resources/job-co-details.model';
import { JobCoDetailsElementListComponent } from './job-co-details-element-list/job-co-details-element-list.component';
import { JobCoDetailsFormState } from './job-co-details-form.service';
import { jobCoDetailsQuoteFilterDialogRoute } from './job-co-details-quote-filter-dialog/job-co-details-quote-filter-dialog.component';

export type JobCoFormFlowState = FormFlowState<JobCoSave> & { notFoundConfirmation?: JobCoConfirmationItem[] };

@Component({
  standalone: false,
  selector: 'gh-job-co-details',
  templateUrl: 'job-co-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [JobCoDetailsFormState],
})
export class JobCoDetailsComponent extends AbstractFormComponent<JobCoDetails> implements OnInit, OnDestroy {
  @ViewChild(JobCoDetailsElementListComponent) private jobCoElementList?: JobCoDetailsElementListComponent;

  producingLabel$ = this.form.produceStatus$.pipe(map$(({ processed, total }) =>
    this.translations.get('lbl_change_order_docs_in_producing', {
      totalProduced: toString(processed),
      totalAll: toString(total),
    })));
  isCloud: boolean;
  variations$: Observable<JobCoElement[]>;

  private produceSubscription: Subscription;

  constructor(private route: ActivatedRoute,
              private router: AppRouter,
              private translations: TranslationService,
              private dialogs: DialogService,
              private notifications: NotificationService,
              private waitings: WaitingService,
              private navigations: BuilderNavigationService,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private business: JobCoDetailsBusinessService,
              private printers: PrinterBusinessService,
              public form: JobCoDetailsFormState) {
    super();
  }

  get title(): string {
    return {
      [FormMode.CreateMode]: 'lbl_create_change_order',
      [FormMode.EditMode]: 'lbl_edit_change_order',
      [FormMode.ViewMode]: 'lbl_view_change_order',
    }[this.form.mode];
  }

  get parentTitle(): Maybe<TranslationTuple> {
    return getRouteParentTitle(this.route);
  }

  get completeFlow(): ActionFlow<JobCoFormFlowState> {
    return this.pipelines.complete({
      validateForm: this.commonActions.validateWithEffect(whenFormInvalid, () => this.handleErrors()),
      execute: this.validateAndSaveAction(),
      finish: ({ value, notFoundConfirmation }: JobCoFormFlowState) => {
        this.form.mergeOnProcess(value);

        if (notFoundConfirmation && !isEmpty(notFoundConfirmation)) {
          this.notifications.warning('msg_var_no_job_estimate', {
            content: notFoundConfirmation
              .map((item) => `${padStart(toString(item.costCenterCode), 3, '0')}: ${item.itemCode}`)
              .join('\n'),
          });
        }

        if (this.form.createMode) {
          this.router.switchTo(['/job-admin/jobs', value.jobId, 'change-orders', value.variationId]);
        }
      },
    });
  }

  validateAndSaveAction(): LogicalAction<JobCoFormFlowState> {
    return compoundLogicalAction('save.success', 'save.fail', {
      // recalculate to actualize amountWithGst (only if recalculation needed)
      'start': eitherAction(({ value }: JobCoFormFlowState) => queryRecalculationNeeded(value))
        .bindAs('recalculate', 'checkAmount?'),
      'recalculate': this.commonActions.execute(({ value }: JobCoFormFlowState) => this.business.recalculate(value))
        .bindAs('recalculate.success', 'fail'),
      // after recalculate => check value of amountWithGst
      'recalculate.success': sideEffectAction(({ value }) => this.form.mergeOnRecalculate(value))
        .bindAs('checkAmount?'),
      'checkAmount?': eitherAction(({ value }) => value.derivedInfo.amountWithGst === 0)
        .bindAs('zeroAmount', 'produce?'),
      // show confirmation dialog if amountWith === 0
      'zeroAmount': this.commonActions.yesNoConfirmation('msg_var_zero_amount')
        .bindAs('produce?', 'fail'),
      // whether we need to run producing
      'produce?': eitherAction(({ value }: JobCoFormFlowState) =>
        isPrintQuoteNeeded(value) || isPrintJobOrderNeeded(value) || isPrintApprovedClaimedNeeded(value))
        .bindAs('saveAndProduce', 'save'),
      'saveAndProduce': this.saveAndProduceAction()
        .bindAs('success', 'fail'),
      // run save operation
      'save': this.saveAction()
        .bindAs('success', 'fail'),
      'success': exitAction('save.success'),
      '*': exitAction('save.fail'),
    });
  }

  saveAction(): LogicalAction<JobCoFormFlowState> {
    return this.commonActions.async((state: JobCoFormFlowState) =>
      this.business.save(state.value).pipe(map$((details) => ({
        ...state,
        value: details,
        notFoundConfirmation: details.confirmation,
      }))));
  }

  produceAction(): LogicalAction<JobCoFormFlowState> {
    return compoundLogicalAction('produce.success', 'produce.fail', {
      'start': condAction([
        [() => this.form.status === JobVariationStatus.Quote, 'printQuote'],
        [() => this.form.status === JobVariationStatus.Approved && !this.form.isImmediatelyClaimed, 'printJobOrder'],
        [
          () => this.form.status === JobVariationStatus.Approved && this.form.isImmediatelyClaimed,
          'printApprovedClaimed',
        ],
        [stubTrue, 'success'],
      ]),
      printQuote: this.producePrintQuoteAction().bindAs('get', 'fail'),
      printJobOrder: this.producePrintJobOrderAction().bindAs('get', 'fail'),
      printApprovedClaimed: this.producePrintApprovedClaimedAction().bindAs('get', 'fail'),
      'get': this.commonActions
        .execute(({ value }: JobCoFormFlowState) => this.business.get(value.jobId, value.variationId))
        .bindAs('success', 'fail'),
      'success': exitAction('produce.success'),
      '*': exitAction('produce.fail'),
    });
  }

  producePrintApprovedClaimedAction(): LogicalAction<JobCoFormFlowState> {
    return streamOptionalValueAction((state) =>
      this.startPrintApprovedAndClaimed().pipe(
        // produce should not prevent success flow execution
        // but still needs to show errors
        catchError$((error) => {
          this.notifications.processAll(error);
          return [{}];
        }),
        map$(() => state)));
  }

  producePrintJobOrderAction(): LogicalAction<JobCoFormFlowState> {
    return streamOptionalValueAction((state) =>
      this.replaceJobOrdersAndValidateCostCenters().pipe(
        // produce should not prevent success flow execution
        // but still needs to show errors
        catchError$((error) => {
          this.notifications.processAll(error);
          return [{}];
        }),
        map$(() => state)));
  }

  producePrintQuoteAction(): LogicalAction<JobCoFormFlowState> {
    return streamOptionalValueAction((state) =>
      this.startPrintQuote(state.value.variationId, state.value.letterTemplate!).pipe(
        tap$(() => this.notifications.success('msg_produced_successfully')),
        // produce should not prevent success flow execution
        // but still needs to show errors
        catchError$((error) => {
          this.notifications.processAll(error);
          return [{}];
        }),
        map$(() => state)));
  }

  saveAndProduceAction(): LogicalAction<JobCoFormFlowState> {
    return compoundLogicalAction('saveAndProduce.success', 'saveAndProduce.fail', {
      'start': streamValueAction((state: JobCoFormFlowState) => {
        if (isPrintQuoteNeeded(state.value)) {
          return this.dialogs.openWithInput(jobCoDetailsQuoteFilterDialogRoute, {})
            .afterClosed().pipe(
              filter$(Boolean),
              map$((quoteFilter: any) => ({ ...state, value: mergeJobCoDetailsWithQuoteFilter(state.value, quoteFilter) })));
        } else if (isPrintJobOrderNeeded(state.value) || isPrintApprovedClaimedNeeded(state.value)) {
          return of$(state);
        } else {
          return throwError$(void 0);
        }
      }).bindAs('saveWithProduce', 'saveWithoutProduce'),
      'saveWithProduce': this.saveAction().bindAs('produce', 'fail'),
      'produce': this.produceAction().bindAs('success', 'success'),
      'saveWithoutProduce': this.saveAction().bindAs('success', 'fail'),
      'success': exitAction('saveAndProduce.success'),
      '*': exitAction('saveAndProduce.fail'),
    });
  }

  recalculateFlow(): ActionFlow<JobCoFormFlowState> {
    return this.pipelines.process({
      noSuccessNotification: true,
      validateForm: this.commonActions.validateWithEffect(whenFormInvalid, () => this.handleErrors()),
      execute: this.commonActions.execute(() => this.business.recalculate(this.form.submitValue)),
      finish: ({ value }) => {
        this.form.mergeOnRecalculate(value);
      },
    });
  }

  ngOnInit(): void {
    const { details, printers } = getRouteInitData(this.route);

    this.form.build(details);
    this.isCloud = !!printers.isCloud;
    this.variations$ = this.form.elements$;

    // listen for change of produce status
    this.produceSubscription = this.form
      // changing of produce status affects tracking process ONLY IF job and/or producing flag has been changed
      .producing$.pipe(
        // Event is sent by producing$ stream as result of state change (PRODUCE_STATUS),
        // if we change state in subscribe handler here synchronously we can get undesired behavior:
        // state is not updated as result of PRODUCE_STATUS and we try to update it again.
        // To avoid this problem we move producing into another execution frame,
        delay$(0),
        switchMap$((producing) => producing
          // if orders are producing now => check producing status each 10 seconds
          ? interval$(PRODUCE_STATUS_CHECK_INTERVAL).pipe(
            // check each time if producing has been finished
            switchMap$(() => this.business.getProduceStatus(this.form.jobId, this.form.changeOrderId)))
          // if orders are not producing now => do nothing
          : [EMPTY_PRODUCE_STATUS]))
      .subscribe(
        (nextProduceStatus) => {
          const inProducing = this.form.producingInProcess;
          this.form.setProduceStatus(nextProduceStatus);

          // if producing has finished run dialog flow
          if (inProducing && !this.form.producingInProcess) {
            this.finishProducing();
          }
        },
        this.notifications.processAll,
      );
  }

  ngOnDestroy(): void {
    this.produceSubscription.unsubscribe();
  }

  onMasterInfoChange(snapshot: FormStateSnapshot<JobCoDetailsMasterInfo>): void {
    const oldMasterInfo = getFormStateSnapshotValue(this.form.masterInfo);
    const newMasterInfo = getFormStateSnapshotValue(snapshot);
    const modifiedFields = differentFieldsWith(oldMasterInfo, newMasterInfo);

    this.form.setMasterInfo(snapshot);

    if (modifiedFields.includes('priceBook') && this.form.hasChanges) {
      const confirmation$ = this.dialogs.confirmation(yesNoConfirmation('msg_var_change_price_book'));

      confirmation$.pipe(
        filter$(yesAnswer))
        .subscribe(() => this.form.removeAllElements());

      confirmation$.pipe(
        filter$(noAsDefaultAnswer))
        .subscribe(() => this.form.setMasterInfo(formStateSnapshot(oldMasterInfo)));
    }
  }

  onBottomInfoChange(snapshot: FormStateSnapshot<JobCoDetailsBottomInfo>): void {
    this.form.setBottomInfo(snapshot);
  }

  onElementsAdd(): void {
    const priceBook = this.form.priceBook!;
    this.dialogs.openWithInput(pbComponentItemFinderDialogRoute, <PbComponentItemFinderDialogConfig>{
      multiselection: true,
      priceBookId: priceBook.id,
      priceBookName: priceBook.name,
      jobId: this.form.jobId,
      jobNumber: this.form.jobNumber,
      isNumericSelector: true,
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities),
      switchMap$((items: PbComponentItemFinderListItem[]) => {
        const duplicatedItems: PbComponentItemFinderListItem[] = items.reduce((acc, item) => {
          const count = item.count || 1;
          const copies = Array(count).fill(null).map(() => ({ ...item }));
          return acc.concat(copies);
        }, [] as PbComponentItemFinderListItem[]);
        return this.business.generateChangeOrderIds(
          this.form.jobId,
          duplicatedItems.filter((item) => !item.isComponent).length,
          duplicatedItems.filter((item) => item.isComponent).length).pipe(
            (this.waitings.operator()),
            map$((generatedIdSets) => jobCoElementsFromFinder(generatedIdSets, duplicatedItems)))
      }))
      .subscribe((elements) => {
        this.form.addElements(elements);
      });
  }

 
  onElementClone(element: JobCoElement): void {
    if (element) {
      this.business.generateChangeOrderIds(
        this.form.jobId,
        element.isComponent ? 0 : 1,  
        element.isComponent ? 1 : 0   
      ).pipe(
        this.waitings.operator(),
        map$((generatedIdSets) => {
          const newId = element.isComponent ? generatedIdSets.componentIds[0] : generatedIdSets.itemIds[0];
          return [{ 
            ...element,
            variationId: element.isComponent ? `component:${newId}` : `item:${newId}`,
            variationItemId: newId,
            dirtyType: DirtyType.New,
            isExistInJob: false,
            origQuantity: 0,
            entityState: void 0,
          }];
        })
      ).subscribe((elements) => {
        this.form.addElements(elements);
      });
    }
  }
  onElementRemove(element: JobCoElement): void {
    const id = getJobCoElementId(element);
    this.dialogs.confirmation(yesNoConfirmation('msg_var_remove_item_component', { code: element.code })).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.form.removeElement(element);
        const elements = this.form.value?.elements;
        const dataRowCount = Array.isArray(elements)
          ? elements.filter((e) => isJobCoElement(e) && !isJobCoElementTotal(e)).length
          : 0;
        this.jobCoElementList?.notifyRowRemovedAfterConfirm(id, dataRowCount === 0);
      });
  }

  onElementsRemoveSelected(elements: JobCoElement[]): void {
    if (!elements || elements.length === 0) {
      return;
    }
    const count = elements.length;
    const confirmation$ = count === 1
      ? this.dialogs.confirmation(yesNoConfirmation('msg_var_remove_item_component', { code: elements[0].code }))
      : this.dialogs.confirmation(
        yesNoConfirmation('msg_bulk_remove_n_items_co', { count: String(count) }));
    confirmation$.pipe(filter$(yesAnswer)).subscribe(() => {
      elements.forEach((element) => {
        this.form.removeElement(element);
      });
      const after = this.form.value?.elements;
      const dataRowCount = Array.isArray(after)
        ? after.filter((e) => isJobCoElement(e) && !isJobCoElementTotal(e)).length
        : 0;
      this.jobCoElementList?.notifyRowsRemovedAfterConfirm(elements, dataRowCount === 0);
    });
  }

  onElementSetField(element: JobCoElement, field: string, value: any): void {
    this.form.setElementField(element, field, value);
  }

  onElementMoveCreditDebit(element: JobCoElement): void {
    if (!element.isExistInJob) {
      this.notifications.error('msg_var_no_job_estimate_move');
      return;
    }

    if (isJobCoElementNotEnoughQuantityForMove(element)) {
      this.dialogs.confirmation(yesNoConfirmation('msg_var_not_enough_negative', {
        quantity: NumberTypes.Quantity.format(element.origQuantity),
      })).pipe(
        filter$(yesAnswer))
        .subscribe(() => this.form.moveElementCreditDebit(element));
    } else {
      this.form.moveElementCreditDebit(element);
    }
  }

  onElementViewMemo(element: JobCoElement): void {
    this.dialogs.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
      title: this.translations.get('lbl_memo'),
      text: element.memo,
      buttons: [{ value: 'ok', title: this.translations.get('lbl_ok'), type: 'primary' }],
    });
  }

  onDragRow(elements: JobCoElement[]): void {
    this.form.dragRow(elements);
  }

  onElementFocusedCellChange(): void {
    this.form.resetFocusedCell();
  }

  onApprove(): void {
    this.form.approve();
  }

  onReject(): void {
    this.form.reject();
  }

  onPrintOnProcessChange(isPrintOnProcess: boolean): void {
    this.form.setPrintOnProcess(isPrintOnProcess);
  }

  onPrint(): void {
    if (this.form.dirty) {
      this.notifications.error('msg_save_first');
      return;
    }

    const { status } = this.form;
    if (status === JobVariationStatus.Quote) {
      this.producePrintQuote();
    } else if (status === JobVariationStatus.Approved) {
      this.producePrintApprovedAndClaimed();
    }
  }

  onReverse(): void {
    this.form.reverse();
  }

  onChangeRound(): void {
    this.form.changeRound();
  }

  onPreview(): void {
    this.dialogs.openWithInput(jobCoDetailsQuoteFilterDialogRoute, {})
      .afterClosed().pipe(
      filter$(Boolean),
      switchMap$((quoteFilter: any) =>
        this.business.previewDocument(
          this.form.jobId,
          this.form.changeOrderId,
          mergeJobCoDetailsWithQuoteFilter(this.form.submitValue, quoteFilter)).pipe(
          this.waitings.operator())))
      .subscribe(
        (letter) => {
          this.dialogs.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<any>>{
            title: this.translations.get('lbl_preview_documents'),
            html: [
              this.translations.get('lbl_follow_link_preview'),
              '<br>',
              this.translations.get('lbl_job_change_order'),
              '<br>',
              createLinkFromLetter(letter),
              '<br>',
            ].join(''),
            buttons: [{
              title: this.translations.get('lbl_ok'),
              type: 'primary',
              value: '',
            }],
          });
        },
        this.notifications.processAll);
  }

  onRecalculate(): void {
    const flow = this.recalculateFlow();
    flow.run({
      form: this.form,
      value: this.form.submitValue,
    });
  }

  goToAel(): void {
    this.navigations.goToAel({
      label: this.title,
      entityType: 'JOB_VARIATION',
      entityId: this.form.changeOrderId,
    });
  }

  onSearch(event: string): void {
    if (event) {
      this.form.elements$ = this.variations$.pipe(
        map$((elements: JobCoElement[]) =>
               elements.filter((element) =>
                                 element.description.toLowerCase().includes(event.toLowerCase()) ||
                                 element.code.toLowerCase().includes(event.toLowerCase()))),
      );
      this.form.mergeOnProcess(this.form.value);
    } else {
      this.form.elements$ = this.variations$;
      this.form.mergeOnProcess(this.form.value);
    }
  }

  openContact(): void {
    this.dialogs.openWithInput(contactDetailsDialogRoute, <ContactDetailsDialogConfig>{
      contactType: ContactType.Customer,
      contactId: this.form.customerId,
      parentTitle: 'lbl_job',
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((contact: any) => {
        this.form.setValue({
// @ts-expect-error Legacy typing suppression
          customerId: contact.contactId,
// @ts-expect-error Legacy typing suppression
          customerName: contact.name,
          ...this.form.value,
        });
      });
  }

  private handleErrors(): void {
    if (this.form.isMasterInfoInvalid) {
      this.form.highlightMasterInfo();
      return;
    }

    const invalidElement = this.form.firstInvalidElement;
    if (invalidElement) {
      this.form.goToInvalidElementField(invalidElement);
      this.notifications.error('msg_he_incorrect_data_in_tables');
      return;
    }

    if (this.form.isBottomInfoInvalid) {
      this.form.highlightBottomInfo();
      return;
    }
  }

  private producePrintQuote(): void {
    this.dialogs.openWithInput(jobCoDetailsQuoteFilterDialogRoute, {})
      .afterClosed().pipe(
      filter$(Boolean),
      switchMap$((filter: JobCoDetailsQuoteFilter) =>
        this.business.produceChangeOrderDoc(
          this.form.jobId,
          this.form.changeOrderId,
          mergeJobCoDetailsWithQuoteFilter(this.form.submitValue, filter)).pipe(
          (this.waitings.operator()))),
      switchMap$((coQuotationDoc) => this.sendDocuments(
        this.form.changeOrderId,
        usePrinterFromDeliveryMethod(this.form.deliveryBy),
        { coQuotationDoc })))
      .subscribe(
        () => {
          this.notifications.success('msg_produced_successfully');
        },
        this.notifications.processAll);
  }

  private producePrintApprovedAndClaimed(): void {
    this.startPrintApprovedAndClaimed().pipe(
      switchMap$(() => this.business.getProduceStatus(this.form.jobId, this.form.changeOrderId)))
      .subscribe(
        (produceStatus) => {
          this.form.setProduceStatus(produceStatus);
        },
        this.processErrors.bind(this));
  }

  private startPrintApprovedAndClaimed(): Observable<any> {
    return this.dialogs.confirmation(yesNoConfirmation('msg_var_copy')).pipe(
      map$(yesAnswer),
      switchMap$((printQuoteCopy) =>
        printQuoteCopy
          ? this.dialogs.openWithInput(jobCoDetailsQuoteFilterDialogRoute, {})
          .afterClosed().pipe(
            filter$(Boolean),
            map$(produceRequestForPrintApprovedWithQuoteCopy))
          : of$(produceRequestForPrintApprovedWithoutQuoteCopy())),
      switchMap$((produceRequest) => this.produceAndValidateCostCenters(produceRequest)));
  }

  private produceAndValidateCostCenters(request: JobCoProduceRequest): Observable<JobCoProduceRequest> {
    return this.business.produce(this.form.jobId, this.form.changeOrderId, request).pipe(
      this.waitings.operator(),
      switchMap$(({ affectedCostCentersCount }) =>
        // if there are affected cost centers => we have to ask user about replacing of existing orders
        affectedCostCentersCount > 0
          ? this.dialogs.confirmation(yesNoConfirmation('msg_var_cc_affected', {
            count: toString(affectedCostCentersCount),
          })).pipe(
          map$(yesAnswer),
          // run producing once again if user wants to replace existing orders
          switchMap$((replaceJobOrder) => this.business.produce(this.form.jobId, this.form.changeOrderId, {
            ...request,
            replaceJobOrder,
            validAffectedCostCenters: true,
          }).pipe(this.waitings.operator(),
                  map$(() => ({ ...request, replaceJobOrder: true })))))
          : [request]));
  }

  private replaceJobOrdersAndValidateCostCenters(): Observable<void> {
    return this.business.replaceOrders(this.form.jobId,
                                       this.form.changeOrderId,
                                       { validAffectedCostCenters: false }).pipe(
      (this.waitings.operator()),
      switchMap$(({ affectedCostCentersCount }) =>
        // if there are affected cost centers => we have to ask user about replacing of existing orders
        affectedCostCentersCount > 0
          ? this.dialogs.confirmation(yesNoConfirmation('msg_var_cc_affected', {
            count: toString(affectedCostCentersCount),
          })).pipe(
          filter$(yesAnswer),
          // run producing once again if user wants to replace existing orders
          switchMap$(() => this.business.replaceOrders(this.form.jobId, this.form.changeOrderId, {
            validAffectedCostCenters: true,
          }).pipe(
            this.waitings.operator(),
            map$(() => void 0))))
          : [void 0]));
  }

  private startPrintQuote(changeOrderId: ID, letter: JobCoLetter): Observable<void> {
    // show dialog with link to draft document
    return this.dialogs.openWithInput(
      textMessageDialogRoute,
      <TextMessageDialogConfig<Confirmation>>{
        title: this.translations.get('lbl_preview_final_change_order'),
        html: [
          this.translations.get('lbl_follow_link_preview_change_order'),
          '<br>',
          createLinkFromLetter(letter),
          '<br>',
          this.translations.get('lbl_click_generate_final_document'),
        ].join(''),
        buttons: [{
          title: this.translations.get('lbl_cancel'),
          value: Confirmation.Cancel,
        }, {
          title: this.translations.get('lbl_confirm'),
          value: Confirmation.Yes,
          type: 'primary',
        }],
      },
      { disableClose: true },
    ).afterClosed().pipe(
      filter$(yesAnswer),
      // confirm produce
      switchMap$(() =>
        this.business.confirmProduce(this.form.jobId, changeOrderId, letter).pipe(
          (this.waitings.operator()))),
      switchMap$((confirmedLetter) => this.sendDocuments(
        changeOrderId,
        usePrinterFromDeliveryMethod(this.form.deliveryBy),
        { coQuotationDoc: attachmentFromLetter(confirmedLetter) })));
  }

  private finishProducing(): void {
    // when producing has finished we have to reload document
    this.business.get(this.form.jobId, this.form.changeOrderId).pipe(
      this.waitings.operator())
      // and start send documents flow (either via mail or e-mail)
      .subscribe((details) => {
        this.form.mergeOnProcess(details);
        this.generateAndSendDocuments(this.form.changeOrderId, usePrinterForAffectedOrders(this.form.value));
      });
  }

  private generateAndSendDocuments(changeOrderId: ID,
                                   usePrinter: JobCoUsePrinter): void {
    // generate documents
    combineLatest$(this.business.generateDocuments(this.form.jobId, changeOrderId)).pipe(
      (this.waitings.operator()),
      switchMap$(([documentSet]) => this.sendDocuments(changeOrderId, usePrinter, documentSet)))
      .subscribe(
        () => this.notifications.success('msg_produced_successfully'),
        this.notifications.processAll);
  }

  private sendDocuments(changeOrderId: ID,
                        usePrinter: JobCoUsePrinter,
                        documentSet: JobCoDetailsGeneratedDocumentSet): Observable<any> {
    if (isDocumentSetEmpty(documentSet)) {
      return throwError$(MessageBasket.error('msg_no_docs_produced'));
    }
    // generate documents
    return this.dialogs.openWithInput(
      textMessageDialogRoute,
      <TextMessageDialogConfig<Confirmation>>{
        title: this.translations.get('lbl_preview_documents'),
        html: flatten([
          this.translations.get('lbl_follow_link_preview'),
          '<br>',
          documentSet.coQuotationDoc
            ? [
              this.translations.get('lbl_job_change_order'),
              '<br>',
              createLinkFromAttachment(documentSet.coQuotationDoc),
              '<br>',
            ]
            : [],
          documentSet.coConfirmationDoc
            ? [
              this.translations.get('lbl_change_order_confirmation'),
              '<br>',
              createLinkFromAttachment(documentSet.coConfirmationDoc),
              '<br>',
            ]
            : [],
          documentSet.coInvoiceDoc
            ? [
              this.translations.get('lbl_invoice'),
              '<br>',
              createLinkFromAttachment(documentSet.coInvoiceDoc),
              '<br>',
            ]
            : [],
          documentSet.progressDrawStatementDoc
            ? [
              this.translations.get('lbl_progress_draw_statement'),
              '<br>',
              createLinkFromAttachment(documentSet.progressDrawStatementDoc),
              '<br>',
            ]
            : [],
          isEmpty(documentSet.jobOrderDocs) ? [] : [this.translations.get('lbl_job_orders'), '<br>'],
          flatMap(documentSet.jobOrderDocs, (doc) => [
            createLinkFromAttachment(doc),
            '<br>',
          ]),
        ]).join(''),
        note: this.translations.get('lbl_click_to_deliver_documents'),
        buttons: [{
          title: this.translations.get('lbl_cancel'),
          value: Confirmation.Cancel,
        }, {
          title: this.translations.get('lbl_confirm'),
          value: Confirmation.Yes,
          type: 'primary',
        }],
      },
      { disableClose: true },
    ).afterClosed().pipe(
      filter$(yesAnswer),
      switchMap$(() => {
        const emailAttachments = jobCoDocumentSetToEmailAttachments(documentSet);
        if (this.form.deliveryBy === ContactDeliveryMethod.Email && !isEmpty(emailAttachments)) {
          return this.dialogs.openWithInput(emailDialogRoute, <EmailDialogConfig>{
            entryPoint: CommunicationEntryPoint.Job,
            jobId: this.form.jobId,
            contactId: this.form.customerId,
            email: {
              contactId: this.form.customerId,
              jobId: this.form.jobId,
              email: this.form.customerEmail,
              communicationType: CommunicationType.Email,
              attachments: emailAttachments,
            },
          }).afterClosed();
        } else {
          return of$({});
        }
      }),
      switchMap$(() => {
        // we should not show print dialog if there is nothing to print
        if (this.form.deliveryBy === ContactDeliveryMethod.Email && !documentSet.replaceJobOrder) {
          return of$<PrinterChooseResult>({});
        } else if (usePrinter !== JobCoUsePrinter.NotRequired) {
          // check if we have orders to email
          const hasOrdersToEmail = documentSet.replaceJobOrder && this.form.hasOrdersToEmail;

          if (this.isCloud) {
            return this.printers
              .choosePrinter({
                finder: {
                  title: 'lbl_select_printer_for_producing',
                },
                optional: hasOrdersToEmail && usePrinter === JobCoUsePrinter.Optional,
                optionalMessage: 'msg_var_no_printer_email',
                cancelMessage: hasOrdersToEmail ? 'msg_var_cancel_printer_email' : void 0,
              });
          } else {
            return of$<PrinterChooseResult>({});
          }
        } else {
          return of$<PrinterChooseResult>({});
        }
      }),
      switchMap$(({ authCode, printer }) => {
        if (this.isCloud) {
          return this.business.printDocuments(
            this.form.jobId,
            changeOrderId,
            jobCoPrintRequestFromGeneratedDocumentSet(documentSet, printer, authCode)).pipe(
            (this.waitings.operator()));
        } else {
          this.business.printDocuments(
            this.form.jobId,
            changeOrderId,
            jobCoPrintRequestFromGeneratedDocumentSet(documentSet)).pipe(
            (this.waitings.operator()))
            .subscribe(
              ({ s3Link }) => this.showReportDialog(s3Link),
              this.notifications.processAll);
          return of$({});
        }
      }));
  }

  private processErrors(errors: any): void {
    // if producing is in progress switch to producing mode, otherwise run general error handling
    if (isMessageBasket(errors) && errors.has('msg_change_order_docs_in_producing')) {
      this.business.getProduceStatus(this.form.jobId, this.form.changeOrderId).pipe(
        this.waitings.operator())
        .subscribe((status) => this.form.setProduceStatus(status));
    } else {
      this.notifications.processAll(errors);
    }
  }

  private showReportDialog(link: string): Observable<void> {
    return this.dialogs.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
      title: this.translations.get('Link to Print Change Orders'),
      html: [
        this.translations.get('Please follow the link below to print Change Orders'),
        createLink(link, 'Your file'),
      ].join(' '),
      buttons: [{ value: 'ok', title: this.translations.get('lbl_ok'), type: 'primary' }],
    }).afterClosed();
  }
}
